import z from "zod";
import type { AccountManagerAssignmentBlockedReason } from "@remi/types/account-manager-assignment";
import type { ItelTrade } from "@remi/types/tasks/itel-trade";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

export type AccountManagerTransferTargetRejection =
  | "INACTIVE_OR_NOT_ACCOUNT_MANAGER"
  | "NO_OPEN_PHONE_LINE"
  | "ALREADY_THE_ACCOUNT_MANAGER";
export type AccountManagerSubstituteRejection =
  | "ALREADY_THE_OCCUPANT"
  | "NO_LIVE_OCCUPANT"
  | "NOT_AN_ELIGIBLE_ACCOUNT_MANAGER";
export type AccountManagerTransferWorksiteRejection =
  | "NO_ACTIVE_PROJECTS"
  | "NO_DELEGATE_CANDIDATE"
  | "DELEGATE_POOL_EMPTY";

// "BID_NOT_FOUND" here is a blockedReason, not the unrelated ProjectErrorType.BID_NOT_FOUND below.
export type { AccountManagerAssignmentBlockedReason };

const projectErrorTypeValues = [
  "UNKNOWN",
  "BID_NOT_FOUND",
  "CONTRACTOR_PROJECT_ID_EXISTS",
  "REQUIRED_TASKS_NOT_FOUND",
  "CONTRACTOR_ORGANIZATION_NOT_FOUND",
  "UNEXPECTED_BID_REQUESTS_COUNT",
  "EXTERNAL_QUOTES_NOT_FOUND",
  "ASSESSMENT_NOT_FOUND",
  "INVOICE_BUCKET_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "PROJECT_ID_INVALID",
  "PROJECT_DATA_CORRUPTION",
  "CHECKLIST_SCHEMA_NOT_FOUND",
  "CHECKLIST_SUBMISSION_NOT_FOUND",
  "CHECKLIST_SUBMISSION_NOT_FOUND_FOR_PROJECT",
  "CHECKLIST_SUBMISSION_LINEAGE_UNRESOLVED",
  "CHECKLIST_SUBMISSION_NOT_EDITABLE",
  "CHECKLIST_SUBMISSION_CLOSED_AFTER_RTI",
  "CHECKLIST_SUBMISSION_SUPERSEDED",
  "CHECKLIST_SUBMISSION_NOT_REVIEWABLE",
  "CHECKLIST_SUBMISSION_NOT_REOPENABLE",
  "SEED_CHECKLIST_FORBIDDEN_IN_PRODUCTION",
  "CHECKLIST_APPROVE_ALL_FORBIDDEN_IN_PRODUCTION",
  "SEED_CHECKLIST_DID_NOT_CONVERGE",
  "SEED_CHECKLIST_TASK_NOT_COMPLETED",
  "SEED_CHECKLIST_PHOTOS_NOT_ATTACHED",
  "SEED_QA_CHECKLIST_NO_PHOTOS",
  "SEED_QA_CHECKLIST_NO_DRAFTS",
  "CHECKLIST_PHOTO_NOT_FOUND",
  "CHECKLIST_PHOTO_DELETED",
  "CHECKLIST_PHOTO_NOT_UPLOADED",
  "CHECKLIST_PHOTO_NOT_IN_SUBMISSION",
  "CHECKLIST_PHOTO_OWNED_BY_OTHER_USER",
  "CHECKLIST_PHOTO_APPROVAL_CONFLICT",
  "CHECKLIST_THUMBNAIL_CONTENT_TYPE_NEUTRALIZED",
  "CHECKLIST_VIDEO_UPLOAD_NOT_ALLOWED",
  "CHECKLIST_QUESTION_REJECTED",
  "CHECKLIST_QUESTION_NOT_IN_SCHEMA",
  "CHECKLIST_SCHEMA_VERSION_NOT_GREATER",
  "CHECKLIST_SCHEMA_INVALID_CONDITIONS",
  "CHECKLIST_SCHEMA_INVALID_REPEAT_REFERENCES",
  "CHECKLIST_SCHEMA_INVALID_RESERVED_SUFFIX",
  "CHECKLIST_SNAPSHOT_PARSE_FAILED",
  "CHECKLIST_STRUCTURE_NOT_ADDABLE",
  "PARTNER_FLOW_NOT_FOUND",
  "FAILED_TO_UPDATE_PROJECT_DEADLINE",
  "FAILED_TO_CANCEL_CHILD_PROJECTS",
  "FAILED_TO_REVIVE_CHILD_PROJECTS",
  "CHILD_PROJECTS_REVIVE_EMIT_FAILED",
  "FSD_NOT_FOUND",
  "ACTIVE_QUOTES_NOT_FOUND",
  "FAILED_TO_RESET_TASKS",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "BID_REQUEST_NOT_FOUND",
  "MISSING_INVOICE_BID_REQUEST_ID",
  "MISSING_PV_SYSTEM_BID_REQUEST_IDS",
  "PROJECT_SCHEDULE_DATE_NOT_FOUND",
  "SAFETY_ACKNOWLEDGEMENT_ALREADY_EXISTS",
  "CORRECTIONS_PROJECT_INVALID_STATE",
  "ACTIVE_QA_COVERAGE_EXISTS_FOR_PROJECT",
  "QA_PROJECT_INVALID_STATE",
  "QA_CORRECTIONS_SPAWN_PARTIAL_FAILURE",
  "QA_CHECKLIST_SCHEMA_NOT_PUBLISHED",
  "QA_CHECKLIST_SUBMISSION_NOT_AUTOCREATABLE",
  "QA_PROJECT_NOT_LINK_ACCESSIBLE",
  "QA_ASSESSMENT_LOCK_CONTENDED",
  "QA_VISIT_CREATE_LOCK_CONTENDED",
  "QA_WALKTHROUGH_REPLY_ATTEMPT_ABORTED",
  "REINSTALL_PROJECT_NOT_FOUND",
  "SOLAR_SITE_ASSESSMENT_INVALID_STATE",
  "SITE_VISIT_REINSPECTION_INVALID_STATE",
  "PROJECT_PARENT_LINK_INVALID",
  "UNAUTHORIZED_GROUP_COLUMN",
  "ACCOUNT_MANAGER_ASSIGN_RECONCILE_LIMIT_EXCEEDED",
  "CHECKLIST_INGEST_VALIDATION_FAILED",
  "ACCOUNT_MANAGER_ASSIGNMENT_FAILED",
  "ACCOUNT_MANAGER_INELIGIBLE",
  "PARTIAL_SCOPE_REQUIRED_FOR_CATEGORY",
  "WORK_NOT_ALLOWED_ON_SITE_VISIT_BID",
  "ACCOUNT_MANAGER_TRANSFER_INVALID_TARGET",
  "ACCOUNT_MANAGER_SUBSTITUTE_INVALID",
  "ACCOUNT_MANAGER_TRANSFER_UNRESOLVABLE",
  "ACCOUNT_MANAGER_TASK_MOVE_UNVERIFIED",
  "ACCOUNT_MANAGER_HANDOFF_WORKSITE_MISMATCH",
  "ESTIMATE_TASK_NOT_LIVE_FOR_ITEL_REQUEST",
  "ITEL_DOWNLOAD_JWT_SECRET_MISSING",
  "ITEL_REQUEST_FEATURE_DISABLED",
  "ITEL_REVIEW_FEATURE_DISABLED",
  "ITEL_REVIEW_SUBMISSION_NOT_READY",
  "ITEL_REVIEW_REQUIRED",
  "ITEL_REVIEW_AWAITING_STRUCTURES",
  "PROJECT_ROLE_ASSIGNMENT_CONFLICT",
  "PROJECT_ROLE_ASSIGNMENT_REQUIRES_APPOINTMENT",
  "PROJECT_ROLE_REASSIGNMENT_UNVERIFIED",
  "PROJECT_ROLE_NOT_ASSIGNABLE",
  "ACCOUNT_MANAGER_ASSIGNMENT_BLOCKED",
  "INVALID_ACCOUNT_MANAGER",
  "ACCOUNT_MANAGER_ALREADY_ASSIGNED",
  "PROJECT_LIST_CURSOR_INVALID",
  "SEED_MARKER_REQUIRED",
  "SEEDED_PROJECT_RETIRE_FORBIDDEN_IN_PRODUCTION",
  "SEEDED_PROJECT_TASKS_STILL_LIVE",
] as const;
const projectErrorTypeSchema = z.enum(projectErrorTypeValues);
export const ProjectErrorType = projectErrorTypeSchema.enum;
type ProjectErrorType = z.infer<typeof projectErrorTypeSchema>;

export type ProjectErrors =
  | { errorType: typeof ProjectErrorType.UNKNOWN; data: undefined }
  | {
      errorType: typeof ProjectErrorType.BID_NOT_FOUND;
      data: { projectId?: number; bidId?: number; workSiteId?: number };
    }
  | {
      errorType: typeof ProjectErrorType.CONTRACTOR_PROJECT_ID_EXISTS;
      data: { projectId: number; contractorProjectId: string };
    }
  | {
      errorType: typeof ProjectErrorType.REQUIRED_TASKS_NOT_FOUND;
      data: { projectId: number; missingTaskTypes: string[] };
    }
  | { errorType: typeof ProjectErrorType.CONTRACTOR_ORGANIZATION_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof ProjectErrorType.UNEXPECTED_BID_REQUESTS_COUNT; data: { actualCount: number } }
  | { errorType: typeof ProjectErrorType.EXTERNAL_QUOTES_NOT_FOUND; data: { requestForQuoteId?: number } }
  | { errorType: typeof ProjectErrorType.ASSESSMENT_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof ProjectErrorType.INVOICE_BUCKET_NOT_FOUND; data: { projectId: number; partnerFlow?: string } }
  | { errorType: typeof ProjectErrorType.PROJECT_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof ProjectErrorType.PROJECT_ID_INVALID; data: { projectId: string } }
  | {
      errorType: typeof ProjectErrorType.PROJECT_DATA_CORRUPTION;
      data: {
        projectId: number;
        corruptedField: string;
        expectedType: string;
        actualValue: unknown;
      };
    }
  | { errorType: typeof ProjectErrorType.CHECKLIST_SCHEMA_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_NOT_FOUND; data: { submissionId: string } }
  | { errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_NOT_FOUND_FOR_PROJECT; data: { projectId: number } }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_LINEAGE_UNRESOLVED;
      data: { submissionId: string; projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_NOT_EDITABLE;
      data: { submissionId: string; status: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_CLOSED_AFTER_RTI;
      data: { submissionId: string; projectId: number; status: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_SUPERSEDED;
      data: { submissionId: string; projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_NOT_REVIEWABLE;
      data: { submissionId: string; status?: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SUBMISSION_NOT_REOPENABLE;
      data: { submissionId: string; status?: string; newSubmissionId?: string; reason?: string };
    }
  | {
      errorType: typeof ProjectErrorType.SEED_CHECKLIST_FORBIDDEN_IN_PRODUCTION;
      data: { projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_APPROVE_ALL_FORBIDDEN_IN_PRODUCTION;
      data: { submissionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.SEED_CHECKLIST_DID_NOT_CONVERGE;
      data: { projectId: number; rounds: number };
    }
  | {
      errorType: typeof ProjectErrorType.SEED_CHECKLIST_TASK_NOT_COMPLETED;
      data: { projectId: number; taskStatus: string | null };
    }
  | {
      errorType: typeof ProjectErrorType.SEED_CHECKLIST_PHOTOS_NOT_ATTACHED;
      data: { projectId: number; questionId: string };
    }
  | { errorType: typeof ProjectErrorType.SEED_QA_CHECKLIST_NO_PHOTOS; data: { projectId: number } }
  | { errorType: typeof ProjectErrorType.SEED_QA_CHECKLIST_NO_DRAFTS; data: { projectId: number; statuses: string } }
  | { errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_NOT_FOUND; data: { submissionId: string; photoId: string } }
  | { errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_DELETED; data: { submissionId: string; photoId: string } }
  | { errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_NOT_UPLOADED; data: { submissionId: string; photoId: string } }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_NOT_IN_SUBMISSION;
      data: { submissionId: string; photoId: string; photoSubmissionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_OWNED_BY_OTHER_USER;
      data: { submissionId: string; photoId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_PHOTO_APPROVAL_CONFLICT;
      data: { submissionId: string; photoId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_THUMBNAIL_CONTENT_TYPE_NEUTRALIZED;
      data: { fileId: string; storedContentType: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_VIDEO_UPLOAD_NOT_ALLOWED;
      data: { submissionId: string; contentType: string; checklistType: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_QUESTION_REJECTED;
      data: { submissionId: string; questionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_QUESTION_NOT_IN_SCHEMA;
      data: { submissionId: string; questionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SCHEMA_VERSION_NOT_GREATER;
      data: { latestVersion: string; requestedVersion: string };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SCHEMA_INVALID_CONDITIONS;
      data: {
        issues: Array<{
          code: "CONDITION_TARGET_MISSING" | "CONDITION_FORWARD_REFERENCE";
          sectionId: string;
          questionId: string;
          conditionQuestionId: string;
        }>;
      };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SCHEMA_INVALID_REPEAT_REFERENCES;
      data: {
        issues: Array<
          | { code: "REPEAT_TARGET_MISSING"; questionId: string; countQuestionId: string }
          | { code: "REPEAT_TARGET_NOT_NUMBER"; questionId: string; countQuestionId: string; actualType: string }
          | { code: "REPEAT_FORWARD_REFERENCE"; questionId: string; countQuestionId: string }
          | { code: "REPEAT_NESTED"; questionId: string; countQuestionId: string }
          | { code: "REPEAT_COUNT_MISMATCH"; questionId: string; countQuestionId: string }
        >;
      };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SCHEMA_INVALID_RESERVED_SUFFIX;
      data: { issues: Array<{ id: string; kind: "question" | "section" }> };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_SNAPSHOT_PARSE_FAILED;
      data: { submissionId?: string; schemaId?: string; zodIssueCount: number };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_STRUCTURE_NOT_ADDABLE;
      data: { submissionId: string; projectId?: number; coveredProjectId?: number; reason: string };
    }
  | { errorType: typeof ProjectErrorType.PARTNER_FLOW_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof ProjectErrorType.FAILED_TO_UPDATE_PROJECT_DEADLINE; data: { failedProjectIds: number[] } }
  | { errorType: typeof ProjectErrorType.FAILED_TO_CANCEL_CHILD_PROJECTS; data: { failedProjectIds: number[] } }
  | { errorType: typeof ProjectErrorType.FAILED_TO_REVIVE_CHILD_PROJECTS; data: { failedProjectIds: number[] } }
  | {
      errorType: typeof ProjectErrorType.CHILD_PROJECTS_REVIVE_EMIT_FAILED;
      data: { bidProjectId: number; originalErrorName: string; originalErrorMessage: string };
    }
  | { errorType: typeof ProjectErrorType.FSD_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof ProjectErrorType.ACTIVE_QUOTES_NOT_FOUND; data: { bidId: number; acceptedRfqCount?: number } }
  | { errorType: typeof ProjectErrorType.FAILED_TO_RESET_TASKS; data: { projectId?: number; failedTaskIds: string[] } }
  | { errorType: typeof ProjectErrorType.REQUEST_FOR_QUOTE_NOT_FOUND; data: { requestForQuoteId: number } }
  | {
      errorType: typeof ProjectErrorType.BID_REQUEST_NOT_FOUND;
      data: { projectId?: number; bidRequestId?: string; pvSystemId?: string };
    }
  | {
      errorType: typeof ProjectErrorType.MISSING_INVOICE_BID_REQUEST_ID;
      data: { projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.MISSING_PV_SYSTEM_BID_REQUEST_IDS;
      data: { projectId: number };
    }
  | { errorType: typeof ProjectErrorType.PROJECT_SCHEDULE_DATE_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof ProjectErrorType.SAFETY_ACKNOWLEDGEMENT_ALREADY_EXISTS;
      data: { projectId: number; acknowledgementId: string };
    }
  | {
      errorType: typeof ProjectErrorType.CORRECTIONS_PROJECT_INVALID_STATE;
      data: {
        projectId: number;
        bidId?: number;
        reason: string;
        correctedProjectIds?: number[];
        correctedProjectBids?: Array<{ projectId: number; bidId: number }>;
      };
    }
  | {
      errorType: typeof ProjectErrorType.ACTIVE_QA_COVERAGE_EXISTS_FOR_PROJECT;
      data: { coveredProjectIds: number[]; qaProjectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_PROJECT_INVALID_STATE;
      data: {
        coveredBidId?: number;
        coveredProjectIds?: number[];
        qaProjectId?: number;
        structureNumber?: number;
        reason: string;
      };
    }
  | {
      errorType: typeof ProjectErrorType.QA_CORRECTIONS_SPAWN_PARTIAL_FAILURE;
      data: { qaProjectId: number; failedCount: number; totalCount: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_CHECKLIST_SCHEMA_NOT_PUBLISHED;
      data: { coveredProjectId: number; partnerFlow: string; projectCategory: string };
    }
  | {
      errorType: typeof ProjectErrorType.QA_CHECKLIST_SUBMISSION_NOT_AUTOCREATABLE;
      data: { qaProjectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_PROJECT_NOT_LINK_ACCESSIBLE;
      data: { projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_ASSESSMENT_LOCK_CONTENDED;
      data: { qaProjectId: number; waitedMs: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_VISIT_CREATE_LOCK_CONTENDED;
      data: { coveredBidId: number; waitedMs: number };
    }
  | {
      errorType: typeof ProjectErrorType.QA_WALKTHROUGH_REPLY_ATTEMPT_ABORTED;
      data: { qaProjectId: number; taskId: string; abortReason: string };
    }
  | { errorType: typeof ProjectErrorType.REINSTALL_PROJECT_NOT_FOUND; data: { bidId: number } }
  | {
      errorType: typeof ProjectErrorType.SOLAR_SITE_ASSESSMENT_INVALID_STATE;
      data: { bidId: number; reason: string };
    }
  | {
      errorType: typeof ProjectErrorType.SITE_VISIT_REINSPECTION_INVALID_STATE;
      data: { projectId: number; reason: string };
    }
  | {
      errorType: typeof ProjectErrorType.PROJECT_PARENT_LINK_INVALID;
      data: { projectId: number; parentProjectId: number; reason: string };
    }
  | {
      errorType: typeof ProjectErrorType.UNAUTHORIZED_GROUP_COLUMN;
      data: { columnId: string };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_ASSIGN_RECONCILE_LIMIT_EXCEEDED;
      data: { candidateCount: number; maxUnattendedAssignments: number };
    }
  | {
      errorType: typeof ProjectErrorType.CHECKLIST_INGEST_VALIDATION_FAILED;
      data: {
        projectId: number;
        violations: {
          kind: "unknown-id" | "incomplete" | "type-mismatch" | "duplicate";
          remiQuestionId: string;
          detail?: string;
        }[];
      };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_ASSIGNMENT_FAILED;
      data: { workSiteId: number; projectId: number };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_INELIGIBLE;
      data: { workSiteId: number; personId: string };
    }
  | {
      errorType: typeof ProjectErrorType.PARTIAL_SCOPE_REQUIRED_FOR_CATEGORY;
      data: { category: string };
    }
  | {
      errorType: typeof ProjectErrorType.WORK_NOT_ALLOWED_ON_SITE_VISIT_BID;
      data: { bidId: number; category: string };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_TRANSFER_INVALID_TARGET;
      data: { workSiteId: number; toPersonId: string; reason: AccountManagerTransferTargetRejection };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_SUBSTITUTE_INVALID;
      data: { workSiteId: number; substitutePersonId: string | null; reason: AccountManagerSubstituteRejection };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_TRANSFER_UNRESOLVABLE;
      data: { workSiteId: number; reason: AccountManagerTransferWorksiteRejection };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_TASK_MOVE_UNVERIFIED;
      data: { workSiteId: number; unconvergedTaskIds: string[]; missingTaskIds: string[] };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_HANDOFF_WORKSITE_MISMATCH;
      data: { workSiteId: number; projectId: number; projectWorkSiteId: number };
    }
  | {
      errorType: typeof ProjectErrorType.ESTIMATE_TASK_NOT_LIVE_FOR_ITEL_REQUEST;
      data: { taskId: string };
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_REQUEST_FEATURE_DISABLED;
      data: { taskId: string; trade: ItelTrade };
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_DOWNLOAD_JWT_SECRET_MISSING;
      data: Record<string, never>;
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_REVIEW_FEATURE_DISABLED;
      data: { submissionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_REVIEW_SUBMISSION_NOT_READY;
      data: { submissionId: string; unapprovedQuestionIds: string[] };
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_REVIEW_REQUIRED;
      data: { submissionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.ITEL_REVIEW_AWAITING_STRUCTURES;
      data: { submissionId: string };
    }
  | {
      errorType: typeof ProjectErrorType.PROJECT_ROLE_ASSIGNMENT_CONFLICT;
      data: { projectId: number; role: string; personIds: (string | null)[] };
    }
  | {
      errorType: typeof ProjectErrorType.PROJECT_ROLE_ASSIGNMENT_REQUIRES_APPOINTMENT;
      data: { projectId: number; role: string; personIds: string[] };
    }
  | {
      errorType: typeof ProjectErrorType.PROJECT_ROLE_REASSIGNMENT_UNVERIFIED;
      data: { projectId: number; projectRole: string; unverifiedIds: string[] };
    }
  | {
      errorType: typeof ProjectErrorType.PROJECT_ROLE_NOT_ASSIGNABLE;
      data: { projectId: number; role: string };
    }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_ASSIGNMENT_BLOCKED;
      data: { bidProjectId: number; workSiteId?: number; reason: AccountManagerAssignmentBlockedReason };
    }
  | { errorType: typeof ProjectErrorType.INVALID_ACCOUNT_MANAGER; data: { personId: string } }
  | {
      errorType: typeof ProjectErrorType.ACCOUNT_MANAGER_ALREADY_ASSIGNED;
      data: { personId: string; existingTerritoryId: string };
    }
  | { errorType: typeof ProjectErrorType.PROJECT_LIST_CURSOR_INVALID; data: undefined }
  | { errorType: typeof ProjectErrorType.SEED_MARKER_REQUIRED; data: undefined }
  | { errorType: typeof ProjectErrorType.SEEDED_PROJECT_RETIRE_FORBIDDEN_IN_PRODUCTION; data: undefined }
  | { errorType: typeof ProjectErrorType.SEEDED_PROJECT_TASKS_STILL_LIVE; data: undefined };

export const projectErrorDetails: Record<ProjectErrorType, ErrorDetails> = {
  PROJECT_LIST_CURSOR_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "An MCP list cursor failed to decode or was issued for different filters",
  },
  SEED_MARKER_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Seeded projects were asked to retire under a marker that is not a complete seed marker",
  },
  SEEDED_PROJECT_RETIRE_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A seeded project retire was requested against production",
  },
  SEEDED_PROJECT_TASKS_STILL_LIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Seeded projects still had live tasks after their tasks were deleted, so the retire stopped",
  },
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  CONTRACTOR_PROJECT_ID_EXISTS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Contractor project ID already exists",
  },
  REQUIRED_TASKS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Required tasks not found for project",
  },
  CONTRACTOR_ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor organization not found for project",
  },
  UNEXPECTED_BID_REQUESTS_COUNT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Encountered unexpected number of bid requests",
  },
  EXTERNAL_QUOTES_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "External quotes not found",
  },
  ASSESSMENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Assessment not found for bid",
  },
  INVOICE_BUCKET_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Invoice bucket not found for project",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  PROJECT_ID_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A projectId that cannot identify a project reached a service boundary",
  },
  PROJECT_DATA_CORRUPTION: {
    classification: ErrorClassification.DATABASE_ERROR,
    sentryDescription: "Project history data corruption detected",
  },
  CHECKLIST_SCHEMA_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Checklist schema not found for project",
  },
  CHECKLIST_SUBMISSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Checklist submission not found",
  },
  CHECKLIST_SUBMISSION_NOT_FOUND_FOR_PROJECT: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No checklist submission found for project",
  },
  CHECKLIST_SUBMISSION_LINEAGE_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Checklist submission lineage has no unique live leaf",
  },
  CHECKLIST_SUBMISSION_NOT_EDITABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist submission is not editable",
  },
  CHECKLIST_SUBMISSION_CLOSED_AFTER_RTI: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist submission is closed to edits because CONFIRM_PROJECT_IS_RTI is completed",
  },
  CHECKLIST_SUBMISSION_SUPERSEDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Checklist draft was replaced by a newer draft forked from it, so it no longer accepts writes",
  },
  CHECKLIST_SUBMISSION_NOT_REVIEWABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist submission is not reviewable (must be in SUBMITTED status)",
  },
  SEED_CHECKLIST_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A seeded checklist was requested against production",
  },
  CHECKLIST_APPROVE_ALL_FORBIDDEN_IN_PRODUCTION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Approve-all checklist questions was requested against production",
  },
  SEED_CHECKLIST_DID_NOT_CONVERGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Seeding a checklist ran out of fill rounds before every question was answered",
  },
  SEED_CHECKLIST_TASK_NOT_COMPLETED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Submitting a seeded site visit checklist did not complete the work photos task",
  },
  SEED_CHECKLIST_PHOTOS_NOT_ATTACHED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A seeded checklist question ended up with no photos attached",
  },
  SEED_QA_CHECKLIST_NO_PHOTOS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A QA checklist seed was given no photos to attach",
  },
  SEED_QA_CHECKLIST_NO_DRAFTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A QA checklist seed found no draft checklists on the visit to submit",
  },
  CHECKLIST_SUBMISSION_NOT_REOPENABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist submission is not reopenable (must be in SUBMITTED status)",
  },
  CHECKLIST_PHOTO_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Checklist photo not found",
  },
  CHECKLIST_PHOTO_DELETED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Cannot approve a checklist photo that was deleted, usually by a concurrent reviewer",
  },
  CHECKLIST_PHOTO_NOT_UPLOADED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot approve a checklist photo whose upload has not been confirmed",
  },
  CHECKLIST_PHOTO_NOT_IN_SUBMISSION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist photo not in submission",
  },
  CHECKLIST_PHOTO_OWNED_BY_OTHER_USER: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "You are not able to remove photos uploaded by another user",
  },
  CHECKLIST_PHOTO_APPROVAL_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Checklist photo approval lost a write conflict after all retries",
  },
  CHECKLIST_THUMBNAIL_CONTENT_TYPE_NEUTRALIZED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Checklist photo thumbnail was stored with a browser-executable Content-Type and was neutralized to application/octet-stream",
  },
  CHECKLIST_VIDEO_UPLOAD_NOT_ALLOWED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist video upload is not an accepted video format or the checklist does not accept video",
  },
  CHECKLIST_QUESTION_REJECTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Cannot approve a rejected question until the contractor resubmits",
  },
  CHECKLIST_QUESTION_NOT_IN_SCHEMA: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist question is not in the submission's schema",
  },
  CHECKLIST_SCHEMA_VERSION_NOT_GREATER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist schema version must be greater than the latest existing version",
  },
  CHECKLIST_SCHEMA_INVALID_CONDITIONS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist schema has conditions referencing missing or downstream questions",
  },
  CHECKLIST_SCHEMA_INVALID_REPEAT_REFERENCES: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Checklist schema has repeat questions referencing missing/wrong-type/downstream/nested count questions",
  },
  CHECKLIST_SCHEMA_INVALID_RESERVED_SUFFIX: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist schema has IDs ending in the reserved `__r<n>` clone suffix",
  },
  CHECKLIST_SNAPSHOT_PARSE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Stored submissionSchemaJson did not parse as a ChecklistSchema",
  },
  CHECKLIST_STRUCTURE_NOT_ADDABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A checklist for another structure cannot be added to this checklist's project",
  },
  PARTNER_FLOW_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Partner flow not found for project",
  },
  FAILED_TO_UPDATE_PROJECT_DEADLINE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to update project deadline",
  },
  FAILED_TO_CANCEL_CHILD_PROJECTS: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to cancel child projects",
  },
  FAILED_TO_REVIVE_CHILD_PROJECTS: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to revive child projects",
  },
  CHILD_PROJECTS_REVIVE_EMIT_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Failed to emit rfq/child-projects.revive-requested; children were revived inline as a fallback",
  },
  FSD_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "FSD not found for bid",
  },
  ACTIVE_QUOTES_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active quotes found for bid",
  },
  FAILED_TO_RESET_TASKS: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to reset tasks",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid request not found for project",
  },
  MISSING_INVOICE_BID_REQUEST_ID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invoice bid request ID is required when invoice per PV system is disabled",
  },
  MISSING_PV_SYSTEM_BID_REQUEST_IDS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "PV system bid request IDs are required when invoice per PV system is enabled",
  },
  PROJECT_SCHEDULE_DATE_NOT_FOUND: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project schedule date not found",
  },
  SAFETY_ACKNOWLEDGEMENT_ALREADY_EXISTS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Safety acknowledgement already exists for this project",
  },
  CORRECTIONS_PROJECT_INVALID_STATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Corrections project cannot be created against the requested project in its current state",
  },
  ACTIVE_QA_COVERAGE_EXISTS_FOR_PROJECT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "An active QA visit already covers one of the requested projects",
  },
  QA_PROJECT_INVALID_STATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "QA visit is not in a state that allows this action",
  },
  QA_CORRECTIONS_SPAWN_PARTIAL_FAILURE: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "QA assessment could not create corrections for some covered projects",
  },
  QA_CHECKLIST_SCHEMA_NOT_PUBLISHED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "No QA checklist schema is published for the covered project's partner flow and category",
  },
  QA_CHECKLIST_SUBMISSION_NOT_AUTOCREATABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A QA project reached the submission autocreate path, which cannot resolve its covered category",
  },
  QA_PROJECT_NOT_LINK_ACCESSIBLE: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "A PROJECT_CHECKLIST link targeted a QA visit, which grants no bearer access",
  },
  QA_ASSESSMENT_LOCK_CONTENDED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Another assessment of this QA visit held the lock past the wait budget",
  },
  QA_VISIT_CREATE_LOCK_CONTENDED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Another QA visit creation on this bid held the lock past the wait budget",
  },
  QA_WALKTHROUGH_REPLY_ATTEMPT_ABORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "The attempt flagging an unclear QA walkthrough reply was refused, so the visit reaches the board unflagged",
  },
  REINSTALL_PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No open reinstall project found on the bid for the solar site assessment",
  },
  SOLAR_SITE_ASSESSMENT_INVALID_STATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Solar site assessment cannot be created for the bid in its current state",
  },
  SITE_VISIT_REINSPECTION_INVALID_STATE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Site visit reinspection cannot be created against the requested project in its current state",
  },
  PROJECT_PARENT_LINK_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Proposed Project.parentProjectId link is self-referential, cyclic, or crosses worksites",
  },
  UNAUTHORIZED_GROUP_COLUMN: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "User is not authorized to group by the requested column",
  },
  ACCOUNT_MANAGER_ASSIGN_RECONCILE_LIMIT_EXCEEDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "AM-assignment reconcile candidates exceeded the unattended limit",
  },
  CHECKLIST_INGEST_VALIDATION_FAILED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Checklist submission ingest failed strict validation and nothing was written",
  },
  ACCOUNT_MANAGER_ASSIGNMENT_FAILED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Project fields were saved, but the account-manager assignment failed — safe to retry",
  },
  ACCOUNT_MANAGER_INELIGIBLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Account-manager assignment rejected — person is disabled, non-ACTIVE, or not in the ACCOUNT_MANAGEMENT group",
  },
  PARTIAL_SCOPE_REQUIRED_FOR_CATEGORY: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project creation rejected — category requires an explicit partial-or-full scope",
  },
  WORK_NOT_ALLOWED_ON_SITE_VISIT_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project creation rejected — only site-visit work may be added to a site-visit bid",
  },
  ACCOUNT_MANAGER_TRANSFER_INVALID_TARGET: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Account-manager transfer target is not a line-holding active account manager",
  },
  ACCOUNT_MANAGER_SUBSTITUTE_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Account-manager substitute rejected — already the occupant, or the worksite has no live occupant",
  },
  ACCOUNT_MANAGER_TRANSFER_UNRESOLVABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Account-manager transfer has no worksite to write or no candidate to assign",
  },
  ACCOUNT_MANAGER_TASK_MOVE_UNVERIFIED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Account-manager transfer could not verify that its task assignee write landed",
  },
  ACCOUNT_MANAGER_HANDOFF_WORKSITE_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Handoff resolved a project whose worksite differs from the transferred worksite",
  },
  ESTIMATE_TASK_NOT_LIVE_FOR_ITEL_REQUEST: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "ITEL request rejected — the target task is not a live Create carrier estimate task",
  },
  ITEL_REQUEST_FEATURE_DISABLED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "ITEL request rejected — the submit-itel feature flag is not enabled for this user",
  },
  ITEL_DOWNLOAD_JWT_SECRET_MISSING: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "ITEL download JWT secret is not configured",
  },
  ITEL_REVIEW_FEATURE_DISABLED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "ITEL review rejected — the submit-itel feature flag is not enabled for this user",
  },
  ITEL_REVIEW_SUBMISSION_NOT_READY: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "ITEL review submitted before every checklist question was approved",
  },
  ITEL_REVIEW_REQUIRED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Site visit checklist approval rejected — it needs an ITEL review, not a plain submit",
  },
  ITEL_REVIEW_AWAITING_STRUCTURES: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "ITEL review submitted on a site visit structure that is not the last one awaiting approval",
  },
  PROJECT_ROLE_ASSIGNMENT_CONFLICT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "One setProjectOwners call named the same project role slot twice with different people",
  },
  PROJECT_ROLE_ASSIGNMENT_REQUIRES_APPOINTMENT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "A production owner was assigned on an appointment-gated project without a live site inspector appointment",
  },
  PROJECT_ROLE_REASSIGNMENT_UNVERIFIED: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Project role re-evaluation could not verify that its assignee write landed",
  },
  PROJECT_ROLE_NOT_ASSIGNABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project role is retired and can no longer be assigned or cleared",
  },
  ACCOUNT_MANAGER_ASSIGNMENT_BLOCKED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Account-manager assignment was requested but nothing could be assigned",
  },
  INVALID_ACCOUNT_MANAGER: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Person is not an active account manager",
  },
  ACCOUNT_MANAGER_ALREADY_ASSIGNED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Account manager is already assigned to a different territory",
  },
};
